import { useCallback, useContext, useState } from 'react';
import { AccountContext } from '../AccountContext';
import { toCurrency, toFormattedRate } from '@app/utilities';
import './CreateTransaction.css';
import {
  useGetSpendOptions,
  useMerchantGatewayService,
  useRefreshAccountAuthorisations,
  useRefreshGetDeclinedAuthorizations,
  useRefreshGetSpendOptions,
  useRefreshPendingAuthorizations,
} from '@app/sdk';
import { Segment } from 'semantic-ui-react';

export default function CreateTransaction() {
  const { accountId, setError } = useContext(AccountContext);
  const [amount, setAmount] = useState(250.0);
  const [selectedAmount, setSelectedAmount] = useState<number>();
  const [description, setDescription] = useState('Hotpoint Fridge');
  const [spendOption, setSpendOption] = useState(undefined as any);

  const [authorizing, setAuthorizing] = useState(false);
  const refreshPendingAuthorizations = useRefreshPendingAuthorizations();
  const refreshAccountAuthorisations = useRefreshAccountAuthorisations();
  const refreshDeclinedAuthorizations = useRefreshGetDeclinedAuthorizations();
  const merchantGatewayService = useMerchantGatewayService();
  const onAmountUpdated = (amount) => {
    setSpendOption(undefined);
    setAmount(amount);
  };

  const refreshSpendOptions = useRefreshGetSpendOptions();

  const getSpendOptions = useCallback(() => {
    setSelectedAmount(amount);
    refreshSpendOptions();
  }, [amount, refreshSpendOptions]);

  const onAuthClicked = async () => {
    try {
      setAuthorizing(true);
      await merchantGatewayService.auth(
        accountId,
        amount,
        description,
        spendOption?.spendType,
        spendOption?.spendOptionId
      );
      setTimeout(() => {
        refreshPendingAuthorizations();
        refreshAccountAuthorisations();
        refreshDeclinedAuthorizations();
      }, 1000);
      setAuthorizing(false);
    } catch (error) {
      setError({ source: 'Auth', ...error });
      setAuthorizing(false);
    }
  };


  const { data: spendOptions, isLoading: spendOptionsLoading } = useGetSpendOptions({
    accountId,
    basketAmount: selectedAmount,
  });

  return (
    <Segment>
      <div className="ui grid">
        <div className="four wide column">
          <Segment>
            <h2>Authorise</h2>
            <Segment>
            <div className="ui form">
                <div className="field">
                  <label>Amount</label>
                  <div className="ui labeled input">
                    <div className="ui label">£</div>
                    <input
                      type="number"
                      placeholder="Amount"
                      step=".01"
                      value={amount}
                      onChange={(e) => onAmountUpdated(parseFloat(e.target.value))}
                    />
                  </div>
                </div>
                <div className="field">
                  <label>Description</label>
                  <input
                    type="text"
                    placeholder="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
            </Segment>
            <Segment textAlign={'center'}>
              <button
                type="button"
                className={`ui primary button`}
                style={{ marginBottom: '10px' }}
                onClick={() => onAuthClicked()}>
                Authorise Standard Purchase
              </button>
              <button
                type="button"
                className={`ui primary button`}
                disabled={Number.isNaN(amount) ?? true}
                onClick={() => getSpendOptions()}>
                Get Offers
              </button>
            </Segment>
          </Segment>
        </div>
        <div className="twelve wide column">
          <div className="ui grid">
            <div className="five wide column">
              <Segment>
                <div className="custom-card-header">Product Offers</div>
                <div className="content scrollable">
                  {spendOptionsLoading && <div className="ui active centered inline loader" />}
                  {spendOptions && (
                    <div className="ui segments">
                      {spendOptions.product.purchase.map((o, i) => (
                        <div
                          className={`ui segment spendOption ${
                            spendOption && spendOption.spendOptionId == o.spendOptionId ? 'secondary' : ''
                          }`}
                          key={i}
                          onClick={() => setSpendOption(o)}>
                          Standard Purchase @ {toFormattedRate(o.interestRate)}
                        </div>
                      ))}

                      {spendOptions.product.instalment.map((o) => (
                        <div
                          className={`ui segment spendOption ${
                            spendOption && spendOption.spendOptionId == o.spendOptionId ? 'secondary' : ''
                          }`}
                          key={o.spendOptionId}
                          onClick={() => setSpendOption(o)}>
                          {o.spendType} - {o.duration} months @ {toFormattedRate(o.interestRate)}
                        </div>
                      ))}

                      {spendOptions.product.bnpl.map((o) => (
                        <div
                          className={`ui segment spendOption ${
                            spendOption && spendOption.spendOptionId == o.spendOptionId ? 'secondary' : ''
                          }`}
                          key={o.spendOptionId}
                          onClick={() => setSpendOption(o)}>
                          {o.spendType} - {o.duration} months
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Segment>
            </div>
            <div className="five wide column">
              <Segment>
                <div className="custom-card-header">Promotional Offers</div>
                <div className="content scrollable">
                  {spendOptionsLoading && <div className="ui active centered inline loader" />}
                  {spendOptions && (
                    <div className="ui segments">
                      {spendOptions.promotional.purchase.map((o) => (
                        <div
                          className={`ui segment spendOption ${
                            spendOption && spendOption.spendOptionId == o.spendOptionId ? 'secondary' : ''
                          }`}
                          key={o.spendOptionId}
                          onClick={() => setSpendOption(o)}>
                          Standard Purchase @ {toFormattedRate(o.interestRate)}
                        </div>
                      ))}

                      {spendOptions.promotional.instalment.map((o) => (
                        <div
                          className={`ui segment spendOption ${
                            spendOption && spendOption.spendOptionId == o.spendOptionId ? 'secondary' : ''
                          }`}
                          key={o.spendOptionId}
                          onClick={() => setSpendOption(o)}>
                          {o.spendType} - {o.duration} months @ {toFormattedRate(o.interestRate)}
                        </div>
                      ))}

                      {spendOptions.promotional.bnpl.map((o) => (
                        <div
                          className={`ui segment spendOption ${
                            spendOption && spendOption.spendOptionId == o.spendOptionId ? 'secondary' : ''
                          }`}
                          key={o.spendOptionId}
                          onClick={() => setSpendOption(o)}>
                          {o.spendType} - {o.duration} months
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Segment>
            </div>
            <div className="five wide column">
              <Segment>
                <div className="custom-card-header">Illustration</div>
                <div className="content">
                  {spendOption?.illustration ? (
                    <table className="ui celled table">
                      <tbody>
                        <tr>
                          <td>Monthly Payment</td>
                          <td>{toCurrency(spendOption.illustration.monthlyPayment.value)}</td>
                        </tr>
                        <tr>
                          <td>Interest to Pay</td>
                          <td>{toCurrency(spendOption.illustration.interestToPay.value)}</td>
                        </tr>
                        <tr>
                          <td>Total Repayable</td>
                          <td>{toCurrency(spendOption.illustration.totalRepayable.value)}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <p>N/A</p>
                  )}
                  <button
                    type="button"
                    className={`ui primary button ${authorizing ? 'loading' : ''}`}
                    disabled={spendOption == undefined}
                    onClick={() => onAuthClicked()}>
                    Authorise
                  </button>
                </div>
              </Segment>
            </div>
          </div>
        </div>
      </div>
    </Segment>
  );
}
